/**
 * Per-tab storage for "return to" URLs when using Back buttons.
 * Used by Cart and Event Details pages to return users to their prior in-site page.
 * sessionStorage is per-tab, so opening Cart in a new tab will not borrow another tab's prior page.
 */

const KEY_CART_RETURN_TO = 'cart_return_to';
const KEY_EVENTS_RETURN_TO = 'events_return_to';

const FALLBACK_CART = '/';
const FALLBACK_EVENTS = '/events';

/** Paths we should not store as returnTo for Cart (e.g. same-area pages) */
const CART_EXCLUDED_PATHS = ['/cart', '/checkout'];

/**
 * Validates that a path is a safe in-site path for navigation.
 */
function isValidReturnToPath(path: string | null | undefined): boolean {
  if (!path || typeof path !== 'string') return false;
  const trimmed = path.trim();
  if (trimmed.length === 0 || trimmed.length > 2048) return false;
  if (!trimmed.startsWith('/')) return false;
  if (trimmed.startsWith('//')) return false; // protocol-relative external
  return true;
}

/**
 * Light-weight check that the path points to a known area of the app.
 * This is intentionally coarse: it only verifies the first segment,
 * so dynamic routes like `/event/123` or `/event/test?foo=bar` are
 * treated as valid since the router will handle the `:id` segment.
 */
function isKnownRoute(path: string): boolean {
  const [pathname] = path.split(/[?#]/, 1);

  return (
    pathname === '/' ||
    pathname === '/events' ||
    pathname.startsWith('/event/') ||
    pathname === '/profile' ||
    pathname === '/signup' ||
    pathname === '/cart' ||
    pathname === '/checkout'
  );
}

/**
 * Gets the current in-site URL (pathname + search) for storing as returnTo.
 */
function getCurrentInSitePath(): string {
  return window.location.pathname + window.location.search;
}

/**
 * Checks if the current path should be excluded from being stored for cart.
 */
function isExcludedForCart(path: string): boolean {
  return CART_EXCLUDED_PATHS.some((excluded) => path.startsWith(excluded));
}

/**
 * Store the current page as the return destination for Cart.
 * Call this before navigating to /cart (e.g. from CartIconButton or CartDrawer).
 */
export function storeReturnToForCart(): void {
  try {
    const path = getCurrentInSitePath();
    if (isExcludedForCart(path)) return;
    if (isValidReturnToPath(path)) {
      sessionStorage.setItem(KEY_CART_RETURN_TO, path);
    }
  } catch {
    // sessionStorage can throw in private modes; ignore
  }
}

/**
 * Store the current page as the return destination for Event Details.
 * Call this before navigating to /event/:id (e.g. from MainLayout or ChildRegistrationsList).
 * @param overridePath - Optional full path (e.g. /events?group_id=5&page=3) to store instead of current URL.
 *   Use when the current URL does not reflect pagination/state (e.g. MainLayout pagination is in state only).
 */
export function storeReturnToForEvents(overridePath?: string): void {
  try {
    const path = overridePath ?? getCurrentInSitePath();
    if (isValidReturnToPath(path)) {
      sessionStorage.setItem(KEY_EVENTS_RETURN_TO, path);
    }
  } catch {
    // sessionStorage can throw in private modes; ignore
  }
}

/**
 * Get the stored return destination for Cart and navigate there.
 * Falls back to FALLBACK_CART if missing or invalid.
 * Returns the path that was used (for logging or error handling).
 */
export function getReturnToForCart(): string {
  try {
    const raw = sessionStorage.getItem(KEY_CART_RETURN_TO);
    const trimmed = raw?.trim();
    if (isValidReturnToPath(trimmed) && isKnownRoute(trimmed!)) {
      return trimmed!;
    }
    // If the stored value is missing or invalid, normalize by writing the
    // known-safe fallback so subsequent navigations have a consistent value.
    sessionStorage.setItem(KEY_CART_RETURN_TO, FALLBACK_CART);
  } catch {
    // ignore
  }
  return FALLBACK_CART;
}

/**
 * Get the stored return destination for Event Details and navigate there.
 * Falls back to FALLBACK_EVENTS if missing or invalid.
 */
export function getReturnToForEvents(): string {
  try {
    const raw = sessionStorage.getItem(KEY_EVENTS_RETURN_TO);
    const trimmed = raw?.trim();
    if (isValidReturnToPath(trimmed) && isKnownRoute(trimmed!)) {
      return trimmed!;
    }
    // If the stored value is missing or invalid, normalize by writing the
    // known-safe fallback so subsequent navigations have a consistent value.
    sessionStorage.setItem(KEY_EVENTS_RETURN_TO, FALLBACK_EVENTS);
  } catch {
    // ignore
  }
  return FALLBACK_EVENTS;
}
